import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { PhysicsEngine } from "@/features/physics/PhysicsEngine";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// Counter for unique magnetism IDs
let buttonMagnetIdCounter = 0;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Disable magnetism registration for this button */
  noMagnetism?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, noMagnetism = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const internalRef = React.useRef<HTMLButtonElement>(null);
    const magnetIdRef = React.useRef<string>(`btn-${++buttonMagnetIdCounter}`);
    
    // Combine refs
    const combinedRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        // Update internal ref
        (internalRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        
        // Forward to external ref
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    
    // Register for magnetism (REQ-PHYS-01)
    React.useEffect(() => {
      const element = internalRef.current;
      if (!element || noMagnetism || asChild) return;
      
      // Register element for cursor magnetism
      PhysicsEngine.register(magnetIdRef.current, element);
      
      // Setup resize observer to update bounds on resize
      const resizeObserver = new ResizeObserver(() => {
        if (internalRef.current) {
          PhysicsEngine.updateBounds(magnetIdRef.current, internalRef.current);
        }
      });
      resizeObserver.observe(element);
      
      // Cleanup
      return () => {
        PhysicsEngine.unregister(magnetIdRef.current);
        resizeObserver.disconnect();
      };
    }, [noMagnetism, asChild]);
    
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={combinedRef} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
